import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';

import { isValidEmail } from '~/modules/helpers';

import { forgotPassword, userPageLoaded } from '~/actions';
import { STATUS } from '~/literals';

import { ForgotPasswordFormValidationMessages, ForgotPasswordParams, RootState, UserState } from '~/types';

interface ForgotPasswordFormProps {
  onSignUpClicked: (event: React.MouseEvent<HTMLElement>) => void;
  onSignInClicked: (event: React.MouseEvent<HTMLElement>) => void;
}

function ForgotPassword({ onSignInClicked, onSignUpClicked }: ForgotPasswordFormProps) {
  const dispatch = useDispatch();
  const status = useSelector<RootState, string | undefined>(({ user }) => user.status);
  const { errorMessage, requestSuccess } = useSelector<RootState, UserState>(({ user }) => user || { errorMessage: '', requestSuccess: false });

  const [validationMode, setValidationMode] = useState(false);
  const [forgotPasswordParams, setForgotPasswordParams] = useState<ForgotPasswordParams>({
    email: '',
  });

  useEffect(() => {
    dispatch(userPageLoaded());
  }, []);

  const [validationMessages, setValidationMessages] =
    useState<ForgotPasswordFormValidationMessages>({
      email: [],
    });

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newForgotPasswordParams = {
      ...forgotPasswordParams,
      email: event.target.value,
    } as ForgotPasswordParams;

    setForgotPasswordParams(newForgotPasswordParams);
    setValidationMessages(getSearchValidationMessages(newForgotPasswordParams));
  };

  const handleSubmitForgotPassword = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const messages = getSearchValidationMessages(forgotPasswordParams);

    setValidationMessages(messages);

    if (messages.email.length) {
      setValidationMode(true);
    } else {
      dispatch(forgotPassword(forgotPasswordParams));
    }
  };

  return (
    <Container sx={{ margin: '0 auto' }} component="main" maxWidth="xs">
      <Box
        sx={{
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Forgot Password
        </Typography>
        <Typography variant="body2">Let's help you reset your password</Typography>
        <Box component="form" noValidate onSubmit={handleSubmitForgotPassword} sx={{ mt: 1, width: '100%' }}>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : ''}
          {requestSuccess ? <Alert severity="info">If your email is registered with us, you will receive an email containing password reset instructions.</Alert> : ''}
          <TextField
            autoComplete="email"
            autoFocus
            error={validationMode && validationMessages.email.length > 0}
            fullWidth
            helperText={
              validationMode && validationMessages.email.length > 0
                ? validationMessages.email.join('\n')
                : null
            }
            id="email"
            label="Email Address"
            margin="normal"
            name="email"
            onChange={handleEmailChange}
            placeholder="Email Address"
            required
            type="email"
          />
          <Button
            disabled={status === STATUS.RUNNING}
            fullWidth
            sx={{ mt: 3, mb: 2 }}
            type="submit"
            variant="contained"
          >
            {status === STATUS.RUNNING ? <CircularProgress color="primary" size={24} /> : 'Submit'}
          </Button>

          <Grid container>
            <Grid item xs>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link onClick={onSignInClicked} sx={{ cursor: 'pointer' }} variant="body2">
                Back to login
              </Link>
            </Grid>
            <Grid item>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link onClick={onSignUpClicked} sx={{ cursor: 'pointer' }} variant="body2">
                Don't have an account? Sign Up
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
}

export default ForgotPassword;

const getSearchValidationMessages = (
  fields: ForgotPasswordParams,
): ForgotPasswordFormValidationMessages => {
  const validationMessages: ForgotPasswordFormValidationMessages = {
    email: [],
  };

  if (!fields.email || !isValidEmail(fields.email)) {
    validationMessages.email.push('* Please enter valid email address.');
  }

  return validationMessages;
};
